/**
 * Central hook for extracting user context from Auth0 token
 * Used by all cache-related operations for user isolation
 * 
 * This hook is critical for the cache security implementation:
 * - Extracts user ID from Auth0 JWT token
 * - Provides consistent user context across the application
 * - Ensures cache operations are user-aware
 * - Prevents cross-user data leakage
 */
import { useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthStore } from '../store/authStore';
import { jwtDecode } from 'jwt-decode';

interface UserContext {
  userId: string | null;
  isAuthenticated: boolean;
  isReady: boolean; // Auth is ready and user ID is available
}

interface Auth0TokenPayload {
  sub: string; // Subject (user ID)
  iat?: number; // Issued at
  exp?: number; // Expiration
  aud?: string | string[]; // Audience
  iss?: string; // Issuer
  [key: string]: any; // Allow additional properties
}

export const useUserContext = (): UserContext => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { accessToken, isApiAuthReady } = useAuthStore();
  
  const userId = useMemo(() => {
    if (!accessToken || !isAuthenticated) {
      return null;
    }
    
    try {
      const decoded = jwtDecode<Auth0TokenPayload>(accessToken);
      
      if (!decoded.sub) {
        console.error('[UserContext] Token missing subject (sub) field');
        return null;
      }
      
      // Auth0 user IDs typically follow the format: "provider|id"
      if (!decoded.sub.includes('|')) {
        console.warn('[UserContext] User ID format may be invalid:', decoded.sub);
      }
      
      return decoded.sub;
    } catch (error) {
      console.error('[UserContext] Failed to decode access token:', error);
      return null;
    }
  }, [accessToken, isAuthenticated]);
  
  const isReady = !isLoading && isApiAuthReady && !!userId;
  
  // Memoize the return object to prevent unnecessary re-renders
  const userContext = useMemo(() => ({
    userId,
    isAuthenticated,
    isReady
  }), [userId, isAuthenticated, isReady]);
  
  // Log user context changes for debugging (only in development)
  const prevUserIdRef = useRef<string | null>(null);
  if (process.env.NODE_ENV === 'development' && prevUserIdRef.current !== userId) {
    console.log('[UserContext] User context changed:', {
      userId: userId ? `${userId.substring(0, 20)}...` : null,
      isAuthenticated,
      isReady,
      isLoading,
      isApiAuthReady,
      hasToken: !!accessToken
    });
    prevUserIdRef.current = userId;
  }
  
  return userContext;
};

/**
 * Helper function to extract user ID from token without React hooks
 * Useful for utility functions and cache managers
 */
export const extractUserIdFromToken = (token: string | null): string | null => {
  if (!token) return null;
  
  try {
    const decoded = jwtDecode<Auth0TokenPayload>(token);
    return decoded.sub || null;
  } catch (error) {
    console.error('[extractUserIdFromToken] Failed to decode token:', error);
    return null;
  }
};

/**
 * Helper function to get current user ID from auth store
 * Useful for utility functions that need user context
 */
export const getCurrentUserId = (): string | null => {
  const { accessToken } = useAuthStore.getState();
  return extractUserIdFromToken(accessToken);
};